<template>
    <div>
      <nav-links v-if="isLoggedIn && (isAdmin || $store.getters.getEntity!=0)" :navs="filteredNavs" :labels="routeNames" brand-text="Core-Decimal Solutions" brand-image="default"></nav-links>
      <router-view v-if="isLoggedIn && (isAdmin || $store.getters.getEntity>0)"/>
      <login-form v-else-if="!isLoggedIn"/>
      <entitySelectorForm v-if="isLoggedIn && !isAdmin && $store.getters.getEntity==0"/>
  
      
      <alert-stack/>
    </div>
    
</template>

<style>
.fade-enter-active, .fade-leave-active {
  transition: opacity 1s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

nav {
  padding: 30px;
}

nav a {
  font-weight: bold;
  color: #2c3e50;
}

nav a.router-link-exact-active {
  color: #42b983;
}
.hand{
  cursor: pointer;
}
.shadow{
    box-shadow: #00000052 0 0 15px 10px !important;
  }
@media print {
  .p-none{
    display:none;
  }
}
</style>
<script>
import navLinks from './components/navLinks.vue';
import loginForm from './components/auth/loginForm.vue';
import alertStack from './components/info/alertStack.vue';
import entitySelectorForm from './components/auth/entitySelectorForm.vue';

const SESSION_TIMEOUT_MS = 10 * 60 * 1000;
const SESSION_WARNING_OFFSET_MS = 30 * 1000;

export default{
  components:{
    navLinks, loginForm, alertStack,entitySelectorForm
  },
  data(){
    return {
      warningTimerId: null,
      expiryTimerId: null,
      axiosInterceptorId: null,
      sessionPromptOpen: false,
      lastActivityAt: Date.now(),
      activityEvents: ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'],
      routeNames:{
        home: "Home Page",
        about:"About Us",
        contact:"Contact Us",
        options:"Options",
        settings:"Setttings",
        profile:"Profile",
        "profile settings":"Profile Settings",
        "entity headers":"Entity Headers",
        login:"Login",
        bank:"Bank",
        "bank account":"Bank Account",
        "bank branch":"Bank Branch",
        "masters":"Masters",
        billable:"Billable",
        entity:"Entity",
        "payee entity":"Payee Entity",
        tax:"Tax",
        "invoice template":"Invoice Template",
        invoice:"Generate Invoice",
        "direct invoice":"Direct Invoice",
        "invoice management":"Invoice Management",
        "view invoice":"View Invoice",
        "administration":"Administration",
        "admin users":"User",
      }
    }
  },
  computed:{
    isLoggedIn(){
      return this.$store.getters.isLoggedIn;
    },
    isAdmin(){
      return this.$store.getters.isAdmin;
    },
    isUserGroupMember(){
      return this.$store.getters.isUserGroupMember;
    },
    filteredNavs(){
      const adminExclude = ['masters', 'invoice management'];
      const regularExclude = ['administration', 'admin users'];
      const routes = this.$router.getRoutes();

      if (this.isAdmin) {
        return routes.filter(r => !adminExclude.includes(r.name));
      }

      return routes.filter(r => !regularExclude.includes(r.name));
    }
  },
  watch: {
    isLoggedIn(newValue) {
      if (newValue) {
        this.startSessionTracking();
      } else {
        this.stopSessionTracking();
      }
      this.enforceRouteAccess();
    },
    isAdmin() {
      this.enforceRouteAccess();
    },
    isUserGroupMember() {
      this.enforceRouteAccess();
    },
    '$route.fullPath'() {
      this.enforceRouteAccess();
    }
  },
  methods: {
    startSessionTracking() {
      this.stopSessionTracking();

      this.activityEvents.forEach((eventName) => {
        window.addEventListener(eventName, this.handleUserActivity, { passive: true });
      });

      if (this.axiosInterceptorId === null && this.axios?.interceptors?.response) {
        this.axiosInterceptorId = this.axios.interceptors.response.use(
          (response) => {
            if (this.isLoggedIn) {
              this.resetSessionTimers();
            }
            return response;
          },
          (error) => {
            if (this.isLoggedIn) {
              this.resetSessionTimers();
            }
            return Promise.reject(error);
          }
        );
      }

      this.resetSessionTimers();
    },
    stopSessionTracking() {
      this.activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, this.handleUserActivity);
      });

      this.clearSessionTimers();
      this.sessionPromptOpen = false;

      if (this.axiosInterceptorId !== null && this.axios?.interceptors?.response) {
        this.axios.interceptors.response.eject(this.axiosInterceptorId);
        this.axiosInterceptorId = null;
      }
    },
    clearSessionTimers() {
      if (this.warningTimerId !== null) {
        clearTimeout(this.warningTimerId);
        this.warningTimerId = null;
      }
      if (this.expiryTimerId !== null) {
        clearTimeout(this.expiryTimerId);
        this.expiryTimerId = null;
      }
    },
    handleUserActivity() {
      if (!this.isLoggedIn) {
        return;
      }
      this.resetSessionTimers();
    },
    resetSessionTimers() {
      this.lastActivityAt = Date.now();
      this.clearSessionTimers();

      const warningDelay = Math.max(SESSION_TIMEOUT_MS - SESSION_WARNING_OFFSET_MS, 0);

      this.warningTimerId = window.setTimeout(() => {
        this.promptToStaySignedIn();
      }, warningDelay);

      this.expiryTimerId = window.setTimeout(() => {
        this.handlePossibleSessionExpiry();
      }, SESSION_TIMEOUT_MS);
    },
    async promptToStaySignedIn() {
      if (!this.isLoggedIn || this.sessionPromptOpen) {
        return;
      }

      this.sessionPromptOpen = true;
      const keepSession = window.confirm('Your session will expire in 30 seconds due to inactivity. Do you want to stay signed in?');

      if (!keepSession) {
        this.sessionPromptOpen = false;
        return;
      }

      try {
        const response = await this.axios.get('/authentication/checklogin');
        if (response?.data?.isLoggedIn) {
          this.$store.commit('setUser', response.data.userData);
          this.$store.commit('setIsLoggedIn', true);
          this.syncEntitySelection(response.data.userData);
          await this.syncOnboardingStateFromServer();
          this.$store.commit('addAlert', {
            title: 'Session Extended',
            message: 'You will stay signed in.',
            type: 'success'
          });
          this.resetSessionTimers();
        } else {
          this.forceLogout('Session Expired', 'Your session has expired. Please log in again.', 'warning');
        }
      } catch (_) {
        this.forceLogout('Session Expired', 'Your session has expired. Please log in again.', 'warning');
      } finally {
        this.sessionPromptOpen = false;
      }
    },
    async handlePossibleSessionExpiry() {
      if (!this.isLoggedIn) {
        return;
      }

      try {
        const response = await this.axios.get('/authentication/checklogin');
        if (response?.data?.isLoggedIn) {
          this.resetSessionTimers();
          return;
        }
      } catch (_) {
        // If check fails at expiry boundary, treat session as expired.
      }

      this.forceLogout('Session Expired', 'Your session expired due to inactivity. Please log in again.', 'warning');
    },
    forceLogout(title, message, type = 'danger') {
      this.$store.commit('setIsLoggedIn', false);
      this.$store.commit('setUser', { name: 'Guest', id: 0 });
      this.$store.commit('setEntity', 0);
      this.$store.commit('addAlert', {
        title,
        message,
        type
      });
    },
    syncEntitySelection(userData) {
      const availableEntities = Array.isArray(userData?.entities) ? userData.entities : [];
      const currentEntity = Number(this.$store.getters.getEntity);
      const canKeepCurrentEntity = availableEntities.some((entity) => Number(entity?.id) === currentEntity);
      this.$store.commit('setEntity', canKeepCurrentEntity ? currentEntity : 0);
    },
    async syncOnboardingStateFromServer() {
      try {
        const response = await this.axios.get('/authentication/onboarding-state');
        const mustCreateFirstEntity = !!response?.data?.data?.mustCreateFirstEntity;
        if (mustCreateFirstEntity) {
          this.$store.commit('setEntity', 0);
        }
      } catch (_) {
        // Keep local fallback behavior if onboarding-state endpoint is unavailable.
      }
    },
    enforceRouteAccess() {
      const userEntities = Array.isArray(this.$store.getters.getUser?.entities)
        ? this.$store.getters.getUser.entities
        : [];
      const mustCreateFirstEntity = this.isLoggedIn
        && !this.isAdmin
        && this.isUserGroupMember
        && userEntities.length === 0;

      if (mustCreateFirstEntity && this.$route.name !== 'home') {
        this.$store.commit('addAlert', {
          title: 'Entity Required',
          message: 'Create your first entity to continue.',
          type: 'warning'
        });
        this.$router.replace('/');
        return;
      }

      const needsAdmin = this.$route.matched.some((record) => record.meta?.requiresAdmin);
      const needsUserGroup = this.$route.matched.some((record) => record.meta?.requiresUserGroup);

      if (needsAdmin && this.isLoggedIn && !this.isAdmin) {
        this.$store.commit('addAlert', {
          title: 'Access Denied',
          message: 'You are not allowed to access this page.',
          type: 'danger'
        });
        this.$router.replace('/');
        return;
      }

      if (needsUserGroup && this.isLoggedIn && !this.isUserGroupMember) {
        this.$store.commit('addAlert', {
          title: 'Access Denied',
          message: 'Only User group members can access this page.',
          type: 'danger'
        });
        this.$router.replace('/');
      }
    }
  },
  async mounted(){
    /* console.log(this.$store.getters.getAxios); */
            await this.axios.get('/authentication/checklogin',)
                  .then(response => {
                    this.$store.commit('setAxiosInstance', this.axios);     
                    this.$store.commit('setUser', response.data.userData);
                    this.$store.commit('setIsLoggedIn', response.data.isLoggedIn);           
                    this.syncEntitySelection(response.data.userData);
                    if (response.data.isLoggedIn) {
                      this.syncOnboardingStateFromServer();
                    }
                    this.$store.commit('addAlert', {
                        title: response.data.title,
                        message: response.data.msg,
                        type: response.data.isLoggedIn ? 'success' : 'danger'
                    });
                    if (response.data.isLoggedIn) {
                      this.startSessionTracking();
                    }
                    this.enforceRouteAccess();
                });
    
  },
  beforeUnmount(){
    this.stopSessionTracking();
  }
}
</script>